/**
 * SkillTreeEdge — 降级线条（v4）
 *
 * 设计哲学：线条是配角，节点是主角
 *
 * 视觉规则：
 *   - 默认：细灰线（1px, opacity 0.2）— 不抢眼
 *   - 主干边：稍粗灰线（1.5px, opacity 0.35）
 *   - 高亮（hover/选中邻域）：彩色线（2px, opacity 0.8）
 *   - confidence 编码：
 *     high → 实线
 *     medium → 实线
 *     low → 虚线 4-3
 *     unverified → 虚线 2-3
 *   - smoothstep 路径（正交圆角）
 */

import { memo, useContext } from 'react';
import { getSmoothStepPath, type EdgeProps } from 'reactflow';
import { CHAIN_COLORS } from '../../utils/chain-constants';
import { HighlightContext } from './HighlightContext';
import { getEdgeRelationLabel } from '../../utils/skilltree-layout';

export interface SkillTreeEdgeData {
  chains: string[];
  isMainTrunk: boolean;
  isSameLayer: boolean;
  confidence?: string;
  dimmed?: boolean;
  highlighted?: boolean;  // 是否高亮（hover/选中邻域）
  zoom?: number;
  relation?: string;  // 边关系类型（用于标签显示）
  showLabel?: boolean;  // 是否显示关系标签
}

const SkillTreeEdge = memo(function SkillTreeEdge({
  source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data,
}: EdgeProps<SkillTreeEdgeData>) {
  const d = data ?? { chains: [], isMainTrunk: false, isSameLayer: false, zoom: 1 };
  const chains = d.chains ?? [];
  const isTrunk = d.isMainTrunk;
  const confidence = d.confidence ?? 'medium';

  // 从 Context 读取邻域高亮状态（避免父级重建 edges 数组）
  const ctx = useContext(HighlightContext);
  const focus = ctx?.neighborhoodIds ?? null;
  const isInNeighborhood = focus ? (focus.has(source) && focus.has(target)) : false;
  const dimmed = focus ? !isInNeighborhood : false;
  const highlighted = isInNeighborhood;

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 10,
  });

  // confidence 虚线
  const dashArray =
    confidence === 'low' ? '4 3' :
    confidence === 'unverified' ? '2 3' :
    undefined;

  // 默认低对比度灰色
  let stroke = '#475569';
  let strokeWidth = isTrunk ? 1.5 : 1;
  let opacity = dimmed ? 0.04 : isTrunk ? 0.35 : 0.2;

  // 高亮时变彩色（取第一个链路色）
  if (highlighted && !dimmed) {
    stroke = chains.length > 0 ? (CHAIN_COLORS[chains[0]] || '#60a5fa') : '#60a5fa';
    strokeWidth = 2;
    opacity = 0.85;
  }

  // 计算边中点（用于显示标签）
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  // 是否显示关系标签（path mode 下显示）
  const showLabel = d.showLabel && d.relation;
  const label = showLabel ? getEdgeRelationLabel(d.relation!) : '';

  return (
    <>
      <path
        d={edgePath}
        stroke={stroke}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={dashArray}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={opacity}
        style={{ transition: 'opacity 0.2s, stroke-width 0.2s' }}
      />
      {showLabel && (
        <g>
          {/* 标签背景 */}
          <rect
            x={midX - 28}
            y={midY - 10}
            width={56}
            height={18}
            rx={4}
            fill="rgba(15, 23, 42, 0.9)"
            stroke={stroke}
            strokeWidth={1}
          />
          {/* 标签文字 */}
          <text
            x={midX}
            y={midY + 4}
            textAnchor="middle"
            fontSize={10}
            fill={highlighted ? stroke : '#94a3b8'}
            fontWeight={500}
            style={{ fontFamily: 'system-ui, sans-serif' }}
          >
            {label}
          </text>
        </g>
      )}
    </>
  );
});

export default SkillTreeEdge;
